import { ApiError } from '@/models/ApiErrorData.ts';
import { PatientListUISettings } from '@/models/UISettingsModel.ts';
import { Schema } from '@/utils/zodUtils.ts';

const SESSION_STORAGE_RETRIEVE_ERROR = 'SESSION_STORAGE_RETRIEVE_ERROR';
const SESSION_STORAGE_STORE_ERROR = 'SESSION_STORAGE_STORE_ERROR';
const SESSION_STORAGE_REMOVE_ERROR = 'SESSION_STORAGE_REMOVE_ERROR';

const getStorageKey = <T extends keyof SessionStorageData>(
  userId: string,
  key: T,
): string => `${key}&${userId}`;

export type SessionStorageData = {
  patientList: PatientListUISettings;
  message: string;
};

export type SessionStorageDataSourceSpec = {
  store: <T extends keyof SessionStorageData>(
    userId: string,
    key: T,
    data: SessionStorageData[T],
  ) => Promise<undefined>;
  retrieve: <T extends keyof SessionStorageData>(
    userId: string,
    key: T,
    schema: Schema<SessionStorageData[T]>,
  ) => Promise<SessionStorageData[T] | undefined>;
  remove: <T extends keyof SessionStorageData>(
    userId: string,
    key: T,
  ) => Promise<undefined>;
};

class SessionStorageDatasource implements SessionStorageDataSourceSpec {
  store = async <T extends keyof SessionStorageData>(
    userId: string,
    key: T,
    data: SessionStorageData[T],
  ): Promise<undefined> => {
    try {
      window.sessionStorage.setItem(
        getStorageKey(userId, key),
        JSON.stringify(data),
      );

      return undefined;
    } catch (_) {
      throw new ApiError({
        statusCode: 400,
        error: SESSION_STORAGE_STORE_ERROR,
        data: undefined,
      });
    }
  };

  retrieve = async <T extends keyof SessionStorageData>(
    userId: string,
    key: T,
    schema: Schema<SessionStorageData[T]>,
  ): Promise<SessionStorageData[T] | undefined> => {
    try {
      const jsonData = window.sessionStorage.getItem(
        getStorageKey(userId, key),
      );
      if (jsonData) {
        return schema.parse(JSON.parse(jsonData));
      }
      return undefined;
    } catch (_) {
      throw new ApiError({
        statusCode: 400,
        error: SESSION_STORAGE_RETRIEVE_ERROR,
        data: undefined,
      });
    }
  };

  remove = async <T extends keyof SessionStorageData>(
    userId: string,
    key: T,
  ): Promise<undefined> => {
    try {
      window.sessionStorage.removeItem(getStorageKey(userId, key));
    } catch (_) {
      throw new ApiError({
        statusCode: 400,
        error: SESSION_STORAGE_REMOVE_ERROR,
        data: undefined,
      });
    }
  };
}

export const sessionStorageDataSource = new SessionStorageDatasource();
